import { store } from "utils/store";
import { TOUR_STORAGE_KEY, TOUR_STORAGE_SCHEMA } from "./constants";
import type { TourPersistedState } from "../types/tour.types";

const PROBE_KEY = "__inkfree_tour_probe__";

const scopeFor = (userId: unknown): string | null => {
  if (typeof userId === "number" && Number.isFinite(userId) && userId > 0) {
    return String(userId);
  }
  if (typeof userId === "string" && userId.trim() !== "") return userId.trim();
  return null;
};

const storageKey = (): string => {
  try {
    const scope = scopeFor(store.getState()?.auth?.userId);
    return scope ? `${TOUR_STORAGE_KEY}::u${scope}` : TOUR_STORAGE_KEY;
  } catch {
    return TOUR_STORAGE_KEY;
  }
};

const emptyState = (): TourPersistedState => ({
  schema: TOUR_STORAGE_SCHEMA,
  completed: {},
  autoStartOptOut: false,
  updatedAt: new Date().toISOString(),
});

let storageAvailable: boolean | null = null;

const isStorageAvailable = (): boolean => {
  if (storageAvailable !== null) return storageAvailable;
  try {
    window.localStorage.setItem(PROBE_KEY, "1");
    window.localStorage.removeItem(PROBE_KEY);
    storageAvailable = true;
  } catch {
    storageAvailable = false;
  }
  return storageAvailable;
};

const isPlainRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const normalise = (raw: unknown): TourPersistedState => {
  if (!isPlainRecord(raw)) return emptyState();
  if (raw.schema !== TOUR_STORAGE_SCHEMA) return emptyState();

  const completed: Record<string, number> = {};
  if (isPlainRecord(raw.completed)) {
    Object.entries(raw.completed).forEach(([tourId, version]) => {
      if (typeof version === "number" && Number.isFinite(version)) {
        completed[tourId] = version;
      }
    });
  }

  return {
    schema: TOUR_STORAGE_SCHEMA,
    completed,
    autoStartOptOut: raw.autoStartOptOut === true,
    updatedAt:
      typeof raw.updatedAt === "string"
        ? raw.updatedAt
        : new Date().toISOString(),
  };
};

export const readTourState = (): TourPersistedState => {
  if (!isStorageAvailable()) return emptyState();
  try {
    const raw = window.localStorage.getItem(storageKey());
    if (!raw) return emptyState();
    return normalise(JSON.parse(raw));
  } catch {
    return emptyState();
  }
};

let quotaWarned = false;

const writeTourState = (state: TourPersistedState): void => {
  if (!isStorageAvailable()) return;
  try {
    window.localStorage.setItem(
      storageKey(),
      JSON.stringify({ ...state, updatedAt: new Date().toISOString() }),
    );
  } catch (error) {
    if (!quotaWarned) {
      quotaWarned = true;
      console.warn(
        "Skipping tour progress persistence:",
        error instanceof Error ? error.message : String(error),
      );
    }
  }
};

export const updateTourState = (
  patch: Partial<TourPersistedState>,
): TourPersistedState => {
  const next = { ...readTourState(), ...patch };
  writeTourState(next);
  return next;
};

export const markTourCompleted = (
  tourId: string,
  version: number,
): TourPersistedState => {
  const current = readTourState();
  return updateTourState({
    completed: { ...current.completed, [tourId]: version },
  });
};

export const clearTourCompletion = (tourId: string): TourPersistedState => {
  const { [tourId]: _cleared, ...rest } = readTourState().completed;
  return updateTourState({ completed: rest });
};
